import { useLocation } from 'react-router-dom';
import { Autocomplete, Box, Breadcrumbs, CircularProgress, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers';
import { EllipsisVertical } from 'lucide-react';
import { useContext, useEffect, useState } from 'react';
import dayjs from '../../../../config/dayjs';

import { Container, MainContainer } from '../../../../components';
import { Menu } from '../Menu/Menu';
import { ProjectContext } from '../../../../providers/projectProvider';
import { getAlertList } from '../../../../services/dashboardService';

import ARROW from '../../../../assets/images/breadcrumb-arrow.svg';
import ARROW_OPEN from '../../../../assets/images/breadcrumb-arrow-open.svg';

import { SampleValue } from '../Dashboard/dashboardPlaceholders';
import { ALERT_STATUS, ALERT_TYPES, getAlertType } from '../Dashboard/alertTypes';
import { AlertTypeFilters } from '../Dashboard/AlertBell';
import { DashboardFilters } from '../Dashboard/DashboardFilters';
import { useDashboardFilters } from '../Dashboard/useDashboardFilters';

// Columns of the approved design (Demo Exam 1004 Dashboard (2).html), with the alert time in its own column
const columns = [
    { id: 'srNo', label: 'Sr No' },
    { id: 'labNo', label: 'Lab No' },
    { id: 'seatNo', label: 'Seat No' },
    { id: 'macAddress', label: 'MAC Address' },
    { id: 'ipAddress', label: 'IP Address' },
    { id: 'details', label: 'Alert Details' },
    { id: 'time', label: 'Time' },
    { id: 'status', label: 'Status' },
    { id: 'action', label: 'Action' },
];

const headSx = { bgcolor: 'grey.100', color: 'grey.600', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', whiteSpace: 'nowrap', px: 1.5, py: 1.25, borderBottom: '1px solid', borderColor: 'grey.300' };

const ALL = 'all';

// Lab No / Seat No dropdown options: each value once, empty ones skipped, sorted so "L2" comes before "L10"
const getOptions = (alerts, field) => {
    const options = [];
    for (const alert of alerts) {
        const value = alert[field];
        if (value && !options.includes(value)) {
            options.push(value);
        }
    }
    return options.sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
}

// Minutes since midnight of a dayjs time, to compare clock times only (the Date filter picks the day)
const toMinutes = (time) => time.hour() * 60 + time.minute();
// A picker value that is empty or still being typed counts as "no limit"
const isTimeSet = (time) => !!time && time.isValid();

// Opened from the dashboard bell / Alert tab with { venueMasterId, examDate } in the route state
export const AlertDetails = () => {

    const location = useLocation();
    const { projectDetails } = useContext(ProjectContext);

    const initialVenueMasterId = location?.state?.venueMasterId;
    const filters = useDashboardFilters({
        initialDate: location?.state?.examDate,
        initialVenueIds: initialVenueMasterId ? [initialVenueMasterId] : [],
    });

    // Venue and date applied by the last "Go" (or the dashboard click); dropdown changes apply on Go
    const [appliedFilter, setAppliedFilter] = useState({ venueMasterId: initialVenueMasterId, examDate: filters.selectedDateStr });
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedType, setSelectedType] = useState(ALL);
    // null = all labs / seats (an empty search box)
    const [selectedLab, setSelectedLab] = useState(null);
    const [selectedSeat, setSelectedSeat] = useState(null);
    // Time frame (From / To); either can be empty = no limit on that side
    const [fromTime, setFromTime] = useState(null);
    const [toTime, setToTime] = useState(null);

    useEffect(() => {
        setSelectedType(ALL);
        setSelectedLab(null);
        setSelectedSeat(null);
        setAlerts([]);
        const fetchAlerts = async () => {
            if (!appliedFilter.venueMasterId) {
                return;
            }
            setLoading(true);
            try {
                const res = await getAlertList(appliedFilter.venueMasterId, appliedFilter.examDate);
                if (res.code === 'OK') {
                    setAlerts(res.data.alerts.map((alert) => ({ ...alert, type: getAlertType(alert.code) })));
                }
            } catch (error) {
                console.log('There is an error while fetching venue alerts', error);
            }
            setLoading(false);
        }
        fetchAlerts();
    }, [appliedFilter]);

    const onGo = () => {
        setAppliedFilter({ venueMasterId: filters.selectedVenueIds[0], examDate: filters.selectedDateStr });
    }

    const venue = filters.allData.venues.find((v) => v.id === appliedFilter.venueMasterId);
    const venueName = venue?.venueName ?? (appliedFilter.venueMasterId ? `Venue ${appliedFilter.venueMasterId}` : '-');

    // Lab / Seat narrow the list first; the type buttons then count and filter within that
    // Same clock time as the Time column (eventTime already carries the local offset)
    const isInTimeFrame = (alert) => {
        if (!isTimeSet(fromTime) && !isTimeSet(toTime)) {
            return true;
        }
        if (!alert.eventTime) {
            return false;
        }
        const alertMinutes = toMinutes(dayjs.utc(alert.eventTime));
        return (!isTimeSet(fromTime) || alertMinutes >= toMinutes(fromTime))
            && (!isTimeSet(toTime) || alertMinutes <= toMinutes(toTime));
    }

    // Filters in order: Time frame -> Lab -> Seat -> type buttons
    const timeAlerts = alerts.filter(isInTimeFrame);
    const labAlerts = timeAlerts.filter((alert) => !selectedLab || alert.labNo === selectedLab);
    const labSeatAlerts = labAlerts.filter((alert) => !selectedSeat || alert.seatNo === selectedSeat);
    const labOptions = getOptions(timeAlerts, 'labNo');
    const seatOptions = getOptions(labAlerts, 'seatNo');

    const countByType = labSeatAlerts.reduce((counts, alert) => { counts[alert.type] = (counts[alert.type] ?? 0) + 1; return counts; }, {});
    const shownAlerts = labSeatAlerts.filter((alert) => selectedType === ALL || alert.type === selectedType);
    const openCount = alerts.filter((alert) => alert.state === 'RAISED').length;

    const onLabChange = (lab) => {
        setSelectedLab(lab);
        setSelectedSeat(null);
        setSelectedType(ALL);
    }
    const onSeatChange = (seat) => {
        setSelectedSeat(seat);
        setSelectedType(ALL);
    }

    const renderTableCell = (columnId, alert, alertIndex) => {
        switch (columnId) {
            case 'srNo':
                return alertIndex + 1;
            case 'ipAddress':
                // From the machine's latest candidate login (examConfig, by MAC); the alert itself has no IP
                return alert.ipAddress ?? '-';
            case 'details':
                return <Box sx={{ display: 'grid', gap: 0.25, whiteSpace: 'normal', minWidth: 260 }}>
                    <Box component='span' sx={{ color: 'error.main', fontWeight: 600 }}>{ALERT_TYPES[alert.type].label}</Box>
                    <Box component='span' sx={{ fontSize: 12, color: 'grey.600' }}>
                        {alert.message ?? alert.title ?? ''}
                    </Box>
                </Box>
            case 'time':
                // eventTime already carries the dashboard's local offset (alert-list), so it is formatted as is
                return alert.eventTime ? dayjs.utc(alert.eventTime).format('hh:mm A') : '-';
            case 'status': {
                const status = ALERT_STATUS[alert.state] ?? { label: alert.state ?? '-', color: 'grey.600' };
                return <Box component='span' sx={{ color: status.color, fontWeight: 600 }}>{status.label}</Box>
            }
            case 'action':
                // Icon only for now; the actions (acknowledge, resolve, activity log, lock) are decided later
                return <IconButton size='small' aria-label={`Actions for ${alert.labNo ?? ''} ${alert.seatNo ?? ''}`}>
                    <EllipsisVertical size={16} />
                </IconButton>
            default:
                return alert[columnId] ?? '-';
        }
    }

    return (
        <MainContainer>
            <Menu>
                <Breadcrumbs aria-label="breadcrumb" separator='' sx={{ gap: 12 }}>
                    <Typography color="text.primary" sx={{ fontWeight: 400, textTransform: 'capitalize' }}>{projectDetails?.projectName}</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW} alt=''></img>
                    </Box>

                    <Typography color="text.primary" sx={{ fontWeight: 500 }}>Alert Details</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW_OPEN} alt='' ></img>
                    </Box>
                </Breadcrumbs>
            </Menu>
            <Container>
                <h1 className='title' style={{ fontSize: 22, fontWeight: 600 }}>Alert Details</h1>

                <DashboardFilters filters={filters} onGo={onGo} isSingleVenue />

                <Typography component="h2" sx={{ fontSize: 15, fontWeight: 600, color: 'primary.main', mb: 1 }}>
                    {venueName} (<SampleValue>{venue?.venueCode ?? '-'}</SampleValue>)
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'grey.600', mb: 1.5 }}>
                    <SampleValue>{`${venue?.city ?? '-'}, ${venue?.state ?? '-'}`}</SampleValue>{` · ${openCount} open of ${alerts.length} alerts`}
                </Typography>

                {!!alerts.length && (
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
                        {/* Searchable; clearing the box = All */}
                        <Autocomplete
                            id="lab"
                            size='small'
                            options={labOptions}
                            getOptionLabel={(lab) => String(lab)}
                            value={selectedLab}
                            onChange={(event, lab) => onLabChange(lab)}
                            sx={{ width: 160 }}
                            renderInput={(params) => <TextField {...params} label="Lab No" placeholder="All" InputLabelProps={{ shrink: true }} />}
                        />
                        <Autocomplete
                            id="seat"
                            size='small'
                            options={seatOptions}
                            getOptionLabel={(seat) => String(seat)}
                            value={selectedSeat}
                            onChange={(event, seat) => onSeatChange(seat)}
                            sx={{ width: 160 }}
                            renderInput={(params) => <TextField {...params} label="Seat No" placeholder="All" InputLabelProps={{ shrink: true }} />}
                        />
                        <TimePicker
                            label='From time'
                            value={fromTime}
                            onChange={setFromTime}
                            slotProps={{ textField: { size: 'small' }, field: { clearable: true } }}
                            sx={{ width: 160 }}
                        />
                        <TimePicker
                            label='To time'
                            value={toTime}
                            onChange={setToTime}
                            slotProps={{ textField: { size: 'small' }, field: { clearable: true } }}
                            sx={{ width: 160 }}
                        />
                        <AlertTypeFilters countByType={countByType} total={labSeatAlerts.length} selectedType={selectedType} onSelect={setSelectedType} />
                    </Box>
                )}

                <Paper sx={{ width: '100%', overflow: 'hidden', boxShadow: 'none', mb: 1 }}>
                    <TableContainer sx={{
                        maxHeight: '60vh',
                        border: '1px solid',
                        borderColor: 'grey.300',
                        borderRadius: 1.5
                    }}>
                        <Table stickyHeader aria-label="alert details table">
                            <TableHead>
                                <TableRow>
                                    {columns.map((column) => <TableCell key={column.id} sx={headSx}>{column.label}</TableCell>)}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading
                                    ? <TableRow>
                                        <TableCell colSpan={columns.length} sx={{ border: 'none', textAlign: 'center' }}>
                                            <CircularProgress size={24} />
                                        </TableCell>
                                    </TableRow>
                                    : !shownAlerts.length
                                        ? <TableRow>
                                            <TableCell colSpan={columns.length} sx={{ border: 'none', textAlign: 'center', color: 'grey.500' }}>
                                                {appliedFilter.venueMasterId ? 'No alerts for this venue' : 'Select a venue to see its alerts'}
                                            </TableCell>
                                        </TableRow>
                                        : shownAlerts.map((alert, alertIndex) => {
                                            return <TableRow hover={false} key={alert.incidentKey}>
                                                {columns.map((column, columnIndex) => {
                                                    return <TableCell key={column.id} sx={{
                                                        px: 1.5,
                                                        py: 1.125,
                                                        fontSize: 13,
                                                        whiteSpace: 'nowrap',
                                                        borderColor: 'grey.300',
                                                        ...(['macAddress', 'ipAddress'].includes(column.id) ? { fontFamily: 'monospace' } : {}),
                                                        // Open alerts get the red left edge, like rows needing attention in the report
                                                        ...(columnIndex === 0 && alert.state === 'RAISED' ? { boxShadow: (theme) => `inset 3px 0 ${theme.palette.error.main}` } : {}),
                                                    }}>
                                                        {renderTableCell(column.id, alert, alertIndex)}
                                                    </TableCell>
                                                })}
                                            </TableRow>
                                        })
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Container>
        </MainContainer>
    );
}
